import type { ReactNode } from 'react';
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { AccountPage } from './features/auth/AccountPage';
import { RecoveryPrintPage } from './features/auth/RecoveryPrintPage';
import { RequireAuth } from './features/auth/RequireAuth';
import { SetupPage } from './features/auth/SetupPage';
import { SignInPage } from './features/auth/SignInPage';
import { WrongAddressNotice } from './features/auth/WrongAddressNotice';
import { ShortcutsPage } from './features/spike/ShortcutsPage';
import { SpikePage } from './features/spike/SpikePage';
import { HomePage } from './routes/HomePage';
import { NotFoundPage } from './routes/NotFoundPage';

function Root() {
  return (
    <>
      <WrongAddressNotice />
      <Outlet />
    </>
  );
}

const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFoundPage });

const guarded = (page: ReactNode) => <RequireAuth>{page}</RequireAuth>;

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => guarded(<HomePage />),
});
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: SetupPage,
});
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInPage,
});
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: () => guarded(<AccountPage />),
});
const recoveryPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/recovery-codes/print',
  component: () => guarded(<RecoveryPrintPage />),
});

// P01 spike pages (temporary; only usable where the server has the spike switched on).
const spikeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spike',
  component: SpikePage,
});
const shortcutsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spike/shortcuts',
  component: ShortcutsPage,
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    homeRoute,
    setupRoute,
    signInRoute,
    accountRoute,
    recoveryPrintRoute,
    spikeRoute,
    shortcutsRoute,
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
